import React from "react";
import { MarketingNav, MarketingPartnerNav, MarketingFooter } from "../../components/Marketing/MarketingChrome/MarketingChrome";
import { partnerDoorHref, TPartnerLinkHosts } from "../../../../domain/models/MarketingPartnerLinks";
import AnchorButton from "../../components/OldFront/Simple/AnchorButton/AnchorButton";
import { SpacingColumn } from "../../components/BuildingBlocks/SpacingColumn";
import { FrontSpacing } from "../../models/FrontSpacing";
import * as MarketingView from "../../../../domain/models/MarketingView";
import * as MarketingStatistic1 from "../../../../domain/models/MarketingStatistic1";
import {
    TMarketingBlock,
    THeroBlock,
    TTrustBarBlock,
    TProseBlock,
    TLiveClaimBlock,
    TStepsBlock,
    TGuaranteeBlock,
    TFaqBlock,
    TPricingBlock,
    TCtaBlock,
    TPeopleBlock,
    TListingsBlock,
    THandledBlock,
    TStatTilesBlock,
    TInlineCtaBlock,
    TStepIcon,
    TPageHeaderBlock,
    TLegalBlock,
    TContactBlock,
    TArticleBlock,
    TArticleListBlock,
    TCaseStudiesBlock,
    TGalleryBlock,
    TPartnerDoorsBlock,
} from "../../../../domain/models/MarketingContent";
import * as PublicListingCard1 from "../../../../domain/models/PublicListingCard1";

const MARKDOWN_LINK = /^\[([^\]]+)\]\((\/[^)\s]*)\)$/;

const renderInline = (text: string, keyPrefix: string): Array<JSX.Element | string> =>
    text.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\(\/[^)\s]*\))/g)
        .filter((part) => part !== "")
        .map((part, i) => {
            if (part.startsWith("**") && part.endsWith("**")) {
                return <strong key={`${keyPrefix}-b${i}`}>{part.slice(2, -2)}</strong>;
            }
            const link = MARKDOWN_LINK.exec(part);
            return link === null
                ? part
                : <a className="marketing__link" href={link[2]} key={`${keyPrefix}-a${i}`}>{link[1]}</a>;
        });

const renderMarkdown = (body: string, keyPrefix: string): Array<JSX.Element> => {
    const lines = body.split("\n").filter((line) => line.trim() !== "");
    const out: Array<JSX.Element> = [];
    let listBuffer: Array<string> = [];

    const flushList = (at: number): void => {
        if (listBuffer.length === 0) {
            return;
        }
        const items = listBuffer;
        listBuffer = [];
        out.push(
            <ul className="marketing__bullets" key={`${keyPrefix}-ul${at}`}>
                {items.map((item, j) =>
                    <li key={`${keyPrefix}-ul${at}-li${j}`}>{renderInline(item, `${keyPrefix}-ul${at}-li${j}`)}</li>,
                )}
            </ul>,
        );
    };

    lines.forEach((line, i) => {
        if (line.startsWith("- ")) {
            listBuffer.push(line.slice(2));
            return;
        }
        flushList(i);

        if (line.startsWith("### ")) {
            out.push(<h3 className="marketing__prose-h3" key={`${keyPrefix}-h3${i}`}>{line.slice(4)}</h3>);
        } else if (line.startsWith("## ")) {
            out.push(<h2 className="marketing__prose-h2" key={`${keyPrefix}-h2${i}`}>{line.slice(3)}</h2>);
        } else {
            out.push(
                <p className="marketing__paragraph" key={`${keyPrefix}-p${i}`}>
                    {renderInline(line, `${keyPrefix}-p${i}`)}
                </p>,
            );
        }
    });

    flushList(lines.length);
    return out;
};

/**
 * `bare` opts out of the white-card treatment, for sections that bring their own dark
 * background (the proof band, the handled panel). Without it the card rule out-specifies
 * them and paints white over white text.
 */
const Eyebrow = (props: { text?: string }): JSX.Element | null =>
    props.text ? <p className="marketing__eyebrow">{props.text}</p> : null;

const STAR_PATH = "M12 2l2.9 6.3 6.9.8-5.1 4.7 1.4 6.8L12 17.3 5.9 20.6l1.4-6.8L2.2 9.1l6.9-.8z";

const TrustpilotStars = (props: { outOfFive: number }): JSX.Element =>
    <span className="marketing__stars" aria-hidden="true">
        <span className="marketing__stars-row marketing__stars-row--empty">
            {[0, 1, 2, 3, 4].map((i) =>
                <svg className="marketing__star" viewBox="0 0 24 24" key={i}><path d={STAR_PATH} /></svg>)}
        </span>
        <span className={`marketing__stars-row marketing__stars-row--full marketing__stars-row--fill-${Math.round(props.outOfFive * 10)}`}>
            {[0, 1, 2, 3, 4].map((i) =>
                <svg className="marketing__star" viewBox="0 0 24 24" key={i}><path d={STAR_PATH} /></svg>)}
        </span>
    </span>;

const Section = (props: React.PropsWithChildren<{ modifier?: string; bare?: boolean }>): JSX.Element =>
    <section
        className={"marketing__section"
            + (props.modifier ? ` marketing__section--${props.modifier}` : "")
            + (props.bare ? " marketing__section--bare" : "")}
    >
        <div className="marketing__container">
            {props.children}
        </div>
    </section>;

const Hero = (props: { block: THeroBlock }): JSX.Element =>
    <section className="marketing__hero">
        <div className="marketing__container marketing__hero-grid">
            <div className="marketing__hero-copy">
                <SpacingColumn spacing={FrontSpacing.MEDIUM_1}>
                    <h1 className="marketing__hero-headline">{props.block.headline}</h1>
                    <p className="marketing__hero-subhead">{props.block.subhead}</p>
                    <ul className="marketing__marks">
                        {HERO_MARKS.map((mark) =>
                            <li className="marketing__mark" key={mark}>
                                <svg className="marketing__mark-tick" viewBox="0 0 24 24" aria-hidden="true">
                                    <path d="M20 6L9 17l-5-5" />
                                </svg>
                                {mark}
                            </li>)}
                    </ul>
                </SpacingColumn>
            </div>

            <div className="marketing__hero-side">
            <div className="marketing__hero-action">
                {/* GET, not POST: the funnel's first step is idempotent, so someone who hits
                    back or reloads never meets a resubmission prompt. */}
                <form className="marketing__capture" method="get" action="/valuation/your-details">
                    <label className="marketing__capture-label" htmlFor="marketing-postcode">
                        {props.block.addressCapture.placeholder}
                    </label>
                    <div className="marketing__capture-row">
                        <input
                            className="front-input-general front-input-general--text-align-left front-input-general--enabled front-input-general--no-error marketing__capture-input"
                            id="marketing-postcode"
                            name="postcode"
                            type="text"
                            autoComplete="postal-code"
                            placeholder={props.block.addressCapture.placeholder}
                            required={true}
                        />
                        <button className="buttonComponent marketing__capture-submit" type="submit">
                            <div className="buttonComponent__button buttonComponent__button--rooster-on">
                                {props.block.addressCapture.buttonLabel}
                            </div>
                        </button>
                    </div>
                    <p className="marketing__capture-reassurance">{props.block.addressCapture.reassurance}</p>
                </form>

                <p className="marketing__hero-phone">
                    Or call <a href={props.block.phone.href}>{props.block.phone.text}</a>
                </p>
            </div>

            {/* Directly below the capture card, on the navy rather than inside the card.
                It keeps the proof next to the decision without making the form busier, and
                it occupies the space the short card used to leave empty. */}
            <a className="marketing__hero-proof" href="https://uk.trustpilot.com/review/sailhomes.co.uk">
                <TrustpilotStars outOfFive={4.8} />
                <span>
                    <strong>4.8 out of 5</strong> from 56 reviews on Trustpilot
                </span>
            </a>

            </div>
        </div>
    </section>;

const HERO_MARKS: Array<string> = [
    "Ombudsman protected",
    "Trading Standards approved code",
    "On Rightmove & Zoopla",
];

const TrustBar = (props: { block: TTrustBarBlock }): JSX.Element =>
    <section className="marketing__trustbar">
        <div className="marketing__container marketing__trustbar-items">
            {props.block.items.map((item) =>
                <img
                    className="marketing__trustbar-logo"
                    key={item.label}
                    src={item.imageUrl}
                    alt={item.alt}
                    width={120}
                    height={40}
                    loading="lazy"
                />,
            )}
        </div>
    </section>;

const Prose = (props: { block: TProseBlock; index: number }): JSX.Element =>
    <Section>
        <div>
            <Eyebrow text={props.block.eyebrow} /><h2 className="marketing__heading">{props.block.heading}</h2>
            <div>{renderMarkdown(props.block.body, `prose${props.index}`)}</div>
            {props.block.tiles ? <Tiles tiles={props.block.tiles} /> : null}
            {props.block.image
                ? <img
                    className="marketing__prose-image"
                    src={props.block.image.url}
                    alt={props.block.image.alt}
                    width={props.block.image.width}
                    height={props.block.image.height}
                    loading="lazy"
                />
                : null}
        </div>
    </Section>;

const Claim = (props: { value: string; label: string; comparison?: string; sourceHref?: string }): JSX.Element =>
    <Section modifier="centred" bare={true}>
        <SpacingColumn spacing={FrontSpacing.SMALL_1}>
            <p className="marketing__claim-row">
                <span className="marketing__claim-value">{props.value}</span>
                <span className="marketing__claim-label">{props.label}</span>
            </p>
            {props.comparison ? <p className="marketing__claim-comparison">{props.comparison}</p> : null}
            {props.sourceHref
                ? <p className="marketing__claim-source">
                    <a className="marketing__link" href={props.sourceHref} target="_blank" rel="noopener noreferrer">
                        Read the reviews
                    </a>
                </p>
                : null}
        </SpacingColumn>
    </Section>;

const LiveClaim = (props: { block: TLiveClaimBlock; statistic: MarketingStatistic1.T | null }): JSX.Element | null => {
    const publishable = props.statistic !== null
        && MarketingStatistic1.isPublishable(props.statistic, {
            minimumSampleSize: props.block.minimumSampleSize,
            saneBand: props.block.saneBand,
        });

    if (publishable && props.statistic !== null) {
        return <Claim
            value={`${MarketingStatistic1.displayDays(props.statistic)} days`}
            label={props.block.label}
            comparison={props.block.comparison}
        />;
    }

    return props.block.fallback
        ? <Claim
            value={props.block.fallback.value}
            label={props.block.fallback.label}
            comparison={props.block.fallback.comparison}
            sourceHref={props.block.fallback.sourceHref}
        />
        : null;
};

const STEP_ICON_PATHS: Record<TStepIcon, string> = {
    postcode: "M12 21s7-5.5 7-11a7 7 0 10-14 0c0 5.5 7 11 7 11zM12 12.5a2.5 2.5 0 100-5 2.5 2.5 0 000 5z",
    valuation: "M3 21h18M5 21V8l7-5 7 5v13M9 21v-6h6v6",
    prepare: "M4 20h16M6 20V9m12 11V9M3 9l9-6 9 6M9.5 13h5",
    market: "M4 19V9l8-6 8 6v10M9 19v-5h6v5M15 3v3",
    offer: "M12 3v18M8 7h6a3 3 0 010 6H9a3 3 0 000 6h7",
    legal: "M12 3v18M5 7h14M7 7l-3 6a3.5 3.5 0 006 0zM17 7l-3 6a3.5 3.5 0 006 0z",
    complete: "M20 7L9.5 17.5 4 12",
};

const StepIcon = (props: { icon?: TStepIcon }): JSX.Element | null =>
    props.icon
        ? <svg
            className="marketing__step-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.7}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
        >
            <path d={STEP_ICON_PATHS[props.icon]} />
        </svg>
        : null;

const Steps = (props: { block: TStepsBlock }): JSX.Element =>
    <Section>
        <SpacingColumn spacing={FrontSpacing.MEDIUM_1}>
            <Eyebrow text={props.block.eyebrow} /><h2 className="marketing__heading">{props.block.heading}</h2>
            <ol className="marketing__steps">
                {/* Icon only, no numbered circle. The two together stacked inside one 44px
                    disc and collided; and the numbered-circle-plus-tiny-icon row is the
                    single most template-looking thing on a page like this - a cold-visitor
                    tester bailed at precisely that pattern, saying "I've seen this exact
                    1, 2, 3 block on every site tonight". The <ol> still conveys order to
                    assistive tech, and three self-sequencing titles convey it visually. */}
                {props.block.steps.map((step) =>
                    <li className="marketing__step" key={step.title}>
                        <span className="marketing__step-number" aria-hidden="true">
                            <StepIcon icon={step.icon} />
                        </span>
                        <div>
                            <h3 className="marketing__step-title">{step.title}</h3>
                            <p className="marketing__paragraph">{step.body}</p>
                        </div>
                    </li>,
                )}
            </ol>
        </SpacingColumn>
    </Section>;

const Guarantee = (props: { block: TGuaranteeBlock }): JSX.Element | null =>
    props.block.enabled
        ? <Section modifier="guarantee">
            <SpacingColumn spacing={FrontSpacing.SMALL_2}>
                <h2 className="marketing__heading">{props.block.name}</h2>
                <p className="marketing__paragraph">{props.block.promise}</p>
                {props.block.termsHref
                    ? <a className="marketing__link" href={props.block.termsHref}>What this covers</a>
                    : null}
            </SpacingColumn>
        </Section>
        : null;

const Faq = (props: { block: TFaqBlock; index: number }): JSX.Element =>
    <Section>
        <div>
            <Eyebrow text={props.block.eyebrow} /><h2 className="marketing__heading">{props.block.heading}</h2>
            <div>
                {props.block.items.map((item, i) =>
                    <details className="marketing__faq-item" key={item.question} open={i === 0}>
                        <summary className="marketing__faq-question">{item.question}</summary>
                        <div className="marketing__faq-answer">
                            {renderMarkdown(item.answer, `faq${props.index}-${i}`)}
                        </div>
                    </details>,
                )}
            </div>
        </div>
    </Section>;

const Pricing = (props: { block: TPricingBlock; index: number }): JSX.Element =>
    <Section>
        <SpacingColumn spacing={FrontSpacing.SMALL_3}>
            <Eyebrow text={props.block.eyebrow} /><h2 className="marketing__heading">{props.block.heading}</h2>
            <div>{renderMarkdown(props.block.intro, `pricing${props.index}`)}</div>
            <dl className="marketing__pricing">
                {props.block.rows.map((row) =>
                    <div className="marketing__pricing-row" key={row.label}>
                        <dt className="marketing__pricing-label">{row.label}</dt>
                        <dd className="marketing__pricing-amount">
                            {row.amount}
                            {row.note ? <span className="marketing__pricing-note">{row.note}</span> : null}
                        </dd>
                    </div>,
                )}
            </dl>
            {props.block.comparison
                ? <p className="marketing__pricing-comparison">
                    {props.block.comparison.label}: {props.block.comparison.amount}
                </p>
                : null}
        </SpacingColumn>
    </Section>;

const Cta = (props: { block: TCtaBlock }): JSX.Element =>
    <Section modifier="cta">
        <SpacingColumn spacing={FrontSpacing.SMALL_3}>
            <Eyebrow text={props.block.eyebrow} /><h2 className="marketing__heading">{props.block.heading}</h2>
            <p className="marketing__paragraph">{props.block.body}</p>
            <div className="marketing__cta-buttons">
                {props.block.ctas.map((cta) =>
                    <AnchorButton
                        key={cta.label}
                        label={cta.label}
                        type={cta.emphasis === "primary" ? "rooster" : "otter"}
                        url={cta.href}
                    />,
                )}
            </div>
        </SpacingColumn>
    </Section>;

const initials = (name: string): string =>
    name
        .split(" ")
        .filter((part) => part.length > 0)
        .map((part) => part[0].toUpperCase())
        .slice(0, 2)
        .join("");

const People = (props: { block: TPeopleBlock }): JSX.Element =>
    <Section>
        <SpacingColumn spacing={FrontSpacing.MEDIUM_1}>
            <div>
                <Eyebrow text={props.block.eyebrow} /><h2 className="marketing__heading">{props.block.heading}</h2>
                <p className="marketing__paragraph">{props.block.intro}</p>
            </div>
            <ul className="marketing__people">
                {props.block.people.map((person) =>
                    <li className="marketing__person" key={person.name}>
                        {person.photo === null
                            ? <div className="marketing__person-photo marketing__person-photo--initials" aria-hidden={true}>
                                {initials(person.name)}
                            </div>
                            : <img
                                className="marketing__person-photo"
                                src={person.photo.url}
                                alt={`${person.name}, ${person.role}`}
                                width={person.photo.size}
                                height={person.photo.size}
                                loading="lazy"
                            />}
                        <div>
                            <p className="marketing__person-name">{person.name}</p>
                            <p className="marketing__person-role">{person.role}</p>
                        </div>
                    </li>,
                )}
            </ul>
        </SpacingColumn>
    </Section>;

const formatPrice = (card: PublicListingCard1.T): string =>
    card.price === null ? "POA" : `£${card.price.toLocaleString("en-GB")}`;

const MINIMUM_LISTINGS_TO_SHOW = 3;

const Listings = (props: { block: TListingsBlock; listings: Array<PublicListingCard1.T> }): JSX.Element | null => {
    const withPhotos = props.listings.filter((card) => card.cover_image_url !== null);

    return withPhotos.length < MINIMUM_LISTINGS_TO_SHOW
        ? null
        : <Section>
            <SpacingColumn spacing={FrontSpacing.MEDIUM_1}>
                <div>
                    <Eyebrow text={props.block.eyebrow} /><h2 className="marketing__heading">{props.block.heading}</h2>
                    <p className="marketing__paragraph">{props.block.intro}</p>
                </div>
                <ul className="marketing__listings">
                    {withPhotos.map((card) =>
                        <li className="marketing__listing marketing__card" key={card.id}>
                            <a className="marketing__listing-link" href={`/listings/${card.id}`}>
                                <img
                                    className="marketing__listing-photo"
                                    src={card.cover_image_url || ""}
                                    alt={card.address_short_string}
                                    width={640}
                                    height={480}
                                    loading="lazy"
                                    decoding="async"
                                />
                                <p className="marketing__listing-price">{formatPrice(card)}</p>
                                <p className="marketing__listing-address">{card.address_short_string}</p>
                            </a>
                        </li>,
                    )}
                </ul>
                <a className="marketing__link" href="/listings">{props.block.ctaLabel}</a>
            </SpacingColumn>
        </Section>;
};

const Tick = (): JSX.Element =>
    <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
        <circle cx="12" cy="12" r="9" /><path d="M8.5 12.2l2.4 2.4 4.6-4.8" />
    </svg>;

const Handled = (props: { block: THandledBlock }): JSX.Element =>
    <section className="marketing__section marketing__section--bare">
        <div className="marketing__container">
            <div className="marketing__handled">
                <Eyebrow text={props.block.eyebrow} /><h2 className="marketing__heading marketing__heading--onDark">{props.block.heading}</h2>
                <p className="marketing__handled-intro">{props.block.intro}</p>
                <ul className="marketing__rows">
                    {props.block.items.map((item, i) =>
                        <li className="marketing__row" key={item}>
                            <Tick />
                            <span>{renderInline(item, `handled${i}`)}</span>
                        </li>,
                    )}
                </ul>
                {props.block.tiles
                    ? <div className="marketing__tiles">
                        {props.block.tiles.map((tile) =>
                            <div className="marketing__tile" key={tile.label}>
                                <p className="marketing__tile-value">{tile.value}</p>
                                <p className="marketing__tile-label">{tile.label}</p>
                            </div>,
                        )}
                    </div>
                    : null}
            </div>
        </div>
    </section>;

const Tiles = (props: { tiles: TStatTilesBlock["tiles"] }): JSX.Element =>
    <div className="marketing__stats">
        {props.tiles.map((tile) =>
            <div className={`marketing__stat marketing__stat--${tile.tone}`} key={tile.key}>
                <p className="marketing__stat-key">{tile.key}</p>
                <p className="marketing__stat-value">{tile.value}</p>
                <p className="marketing__stat-note">{tile.note}</p>
            </div>,
        )}
    </div>;

const StatTiles = (props: { block: TStatTilesBlock }): JSX.Element =>
    <Section>
        <div className="marketing__stats marketing__stats--standalone">
        {(props.block.tiles || []).map((tile) =>
            <div className={`marketing__stat marketing__stat--${tile.tone}`} key={tile.key}>
                <p className="marketing__stat-key">{tile.key}</p>
                <p className="marketing__stat-value">{tile.value}</p>
                <p className="marketing__stat-note">{tile.note}</p>
            </div>,
        )}
        </div>
    </Section>;

const InlineCta = (props: { block: TInlineCtaBlock }): JSX.Element =>
    <Section>
        <div className="marketing__inlineCta">
            <div>
                <p className="marketing__inlineCta-text">{props.block.text}</p>
                {props.block.note ? <p className="marketing__inlineCta-note">{props.block.note}</p> : null}
            </div>
            <AnchorButton label={props.block.ctaLabel} type="rooster" url={props.block.ctaHref} />
        </div>
    </Section>;

const PageHeader = (props: { block: TPageHeaderBlock }): JSX.Element =>
    <Section bare={true} modifier="pageHeader">
        <SpacingColumn spacing={FrontSpacing.MEDIUM_1}>
            <div>
                <Eyebrow text={props.block.eyebrow} />
                <h1 className="marketing__pageTitle">{props.block.heading}</h1>
                {props.block.intro ? <p className="marketing__pageIntro">{props.block.intro}</p> : null}
            </div>
            {props.block.cta
                ? <div><AnchorButton label={props.block.cta.label} type="rooster" url={props.block.cta.href} /></div>
                : null}
        </SpacingColumn>
    </Section>;

const Legal = (props: { block: TLegalBlock; index: number }): JSX.Element =>
    <Section>
        <div className="marketing__legal">
            <p className="marketing__legal-updated">
                Last updated {formatLongDate(props.block.lastUpdated)}
            </p>
            {props.block.sections.map((section, i) =>
                <div className="marketing__legal-section" key={section.heading}>
                    <h2 className="marketing__legal-heading">{section.heading}</h2>
                    {renderMarkdown(section.body, `legal${props.index}-${i}`)}
                </div>,
            )}
        </div>
    </Section>;

const Contact = (props: { block: TContactBlock }): JSX.Element =>
    <Section>
        <SpacingColumn spacing={FrontSpacing.MEDIUM_1}>
            <div>
                <h2 className="marketing__heading">{props.block.heading}</h2>
                <p className="marketing__paragraph">{props.block.intro}</p>
            </div>
            <ul className="marketing__contact">
                {props.block.methods.map((method) =>
                    <li className="marketing__contact-method" key={method.label}>
                        <p className="marketing__contact-label">{method.label}</p>
                        <a className="marketing__contact-value" href={method.href}>{method.value}</a>
                        <p className="marketing__contact-note">{method.note}</p>
                    </li>,
                )}
            </ul>
            <p className="marketing__contact-hours">{props.block.hours}</p>
        </SpacingColumn>
    </Section>;

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const formatLongDate = (iso: string): string => {
    const parts = iso.split("-");
    const monthIndex = Number(parts[1]) - 1;
    return `${Number(parts[2])} ${MONTHS[monthIndex]} ${parts[0]}`;
};

const Article = (props: { block: TArticleBlock; index: number }): JSX.Element =>
    <Section>
        <article className="marketing__article">
            <p className="marketing__article-meta">
                <time dateTime={props.block.publishedOn}>{formatLongDate(props.block.publishedOn)}</time>
                {` · ${props.block.readingMinutes} minute read`}
            </p>
            {/* Never suppressible: an article whose facts have expired says so above its
                first line, not in a footnote the reader reaches after acting on it. */}
            {props.block.staleNotice
                ? <p className="marketing__article-stale">{props.block.staleNotice}</p>
                : null}
            {renderMarkdown(props.block.body, `article${props.index}`)}
        </article>
    </Section>;

const ArticleList = (props: { block: TArticleListBlock }): JSX.Element =>
    <Section bare={true}>
        <ul className="marketing__articles">
            {props.block.articles.map((article) =>
                <li className="marketing__articleCard" key={article.slug}>
                    <a className="marketing__articleCard-link" href={`/blog/${article.slug}`}>
                        <p className="marketing__article-meta">
                            <time dateTime={article.publishedOn}>{formatLongDate(article.publishedOn)}</time>
                            {` · ${article.readingMinutes} minute read`}
                        </p>
                        <h2 className="marketing__articleCard-title">{article.title}</h2>
                        <p className="marketing__paragraph">{article.summary}</p>
                    </a>
                </li>,
            )}
        </ul>
    </Section>;

const CaseStudies = (props: { block: TCaseStudiesBlock }): JSX.Element =>
    <Section>
        <SpacingColumn spacing={FrontSpacing.MEDIUM_1}>
            <div>
                <Eyebrow text={props.block.eyebrow} /><h2 className="marketing__heading">{props.block.heading}</h2>
                <p className="marketing__paragraph">{props.block.intro}</p>
            </div>
            <ul className="marketing__caseStudies">
                {props.block.items.map((item) =>
                    <li className="marketing__caseStudy" key={item.title}>
                        {item.after
                            ? <div className="marketing__caseStudy-shots">
                                {item.before
                                    ? <figure className="marketing__caseStudy-shot">
                                        <img
                                            className="marketing__caseStudy-photo"
                                            src={item.before.url}
                                            alt={item.before.alt}
                                            loading="lazy"
                                        />
                                        <figcaption className="marketing__caseStudy-caption">Before</figcaption>
                                    </figure>
                                    : null}
                                <figure className="marketing__caseStudy-shot">
                                    <img
                                        className="marketing__caseStudy-photo"
                                        src={item.after.url}
                                        alt={item.after.alt}
                                        loading="lazy"
                                    />
                                    {/* Only captioned "After" when there is a before to be
                                        after OF. On its own it is simply a photograph of
                                        the finished house. */}
                                    {item.before
                                        ? <figcaption className="marketing__caseStudy-caption">After</figcaption>
                                        : null}
                                </figure>
                            </div>
                            : null}
                        <p className="marketing__caseStudy-uplift">{item.uplift}</p>
                        <h3 className="marketing__caseStudy-title">{item.title}</h3>
                        <p className="marketing__paragraph">{item.summary}</p>
                        {item.href
                            ? <a className="marketing__caseStudy-link" href={item.href}>Read this case study</a>
                            : null}
                    </li>,
                )}
            </ul>
        </SpacingColumn>
    </Section>;

const Gallery = (props: { block: TGalleryBlock }): JSX.Element =>
    <Section>
        <SpacingColumn spacing={FrontSpacing.MEDIUM_1}>
            <div>
                <Eyebrow text={props.block.eyebrow} /><h2 className="marketing__heading">{props.block.heading}</h2>
                {props.block.intro ? <p className="marketing__paragraph">{props.block.intro}</p> : null}
            </div>
            <ul className="marketing__gallery">
                {props.block.pairs.map((pair) =>
                    <li className="marketing__galleryItem" key={pair.label + pair.after.url}>
                        <div className="marketing__caseStudy-shots">
                            {pair.before
                                ? <figure className="marketing__caseStudy-shot">
                                    <img className="marketing__caseStudy-photo" src={pair.before.url} alt={pair.before.alt} loading="lazy" />
                                    <figcaption className="marketing__caseStudy-caption">Before</figcaption>
                                </figure>
                                : null}
                            <figure className="marketing__caseStudy-shot">
                                <img className="marketing__caseStudy-photo" src={pair.after.url} alt={pair.after.alt} loading="lazy" />
                                {pair.before ? <figcaption className="marketing__caseStudy-caption">After</figcaption> : null}
                            </figure>
                        </div>
                        <p className="marketing__galleryLabel">{pair.label}</p>
                    </li>,
                )}
            </ul>
        </SpacingColumn>
    </Section>;

const PartnerDoors = (props: {
    block: TPartnerDoorsBlock;
    introducerId: string | null;
    hosts: TPartnerLinkHosts;
}): JSX.Element | null => {
    if (props.introducerId === null) {
        return null;
    }
    const introducerId = props.introducerId;

    return <Section modifier="doors">
        <SpacingColumn spacing={FrontSpacing.MEDIUM_1}>
            <div>
                <Eyebrow text={props.block.eyebrow} />
                <h2 className="marketing__heading">{props.block.heading}</h2>
            </div>
            <ul className="marketing__doors">
                {props.block.groups.map((group) =>
                    <li className="marketing__door" key={group.audience}>
                        <h3 className="marketing__door-heading">{group.heading}</h3>
                        <p className="marketing__paragraph">{group.body}</p>
                        <ul className="marketing__door-actions">
                            {group.doors.map((door) =>
                                <li className="marketing__door-action" key={door.service}>
                                    <a
                                        className="marketing__door-link"
                                        href={partnerDoorHref(door.service, group.audience, introducerId, props.hosts, props.block.conveyancingQuoteUrl)}
                                    >
                                        {door.label}
                                    </a>
                                    <p className="marketing__door-body">{door.body}</p>
                                </li>,
                            )}
                        </ul>
                    </li>,
                )}
            </ul>
        </SpacingColumn>
    </Section>;
};

const renderBlock = (
    block: TMarketingBlock,
    index: number,
    statistic: MarketingStatistic1.T | null,
    listings: Array<PublicListingCard1.T>,
    view: MarketingView.T,
): JSX.Element | null => {
    switch (block.kind) {
        case "hero": return <Hero block={block} key={index} />;
        case "pageHeader": return <PageHeader block={block} key={index} />;
        case "legal": return <Legal block={block} index={index} key={index} />;
        case "contact": return <Contact block={block} key={index} />;
        case "article": return <Article block={block} index={index} key={index} />;
        case "articleList": return <ArticleList block={block} key={index} />;
        case "caseStudies": return <CaseStudies block={block} key={index} />;
        case "gallery": return <Gallery block={block} key={index} />;
        case "trustBar": return <TrustBar block={block} key={index} />;
        case "prose": return <Prose block={block} index={index} key={index} />;
        case "claim": return <Claim value={block.value} label={block.label} comparison={block.comparison} sourceHref={block.sourceHref} key={index} />;
        case "liveClaim": return <LiveClaim block={block} statistic={statistic} key={index} />;
        case "steps": return <Steps block={block} key={index} />;
        case "guarantee": return <Guarantee block={block} key={index} />;
        case "faq": return <Faq block={block} index={index} key={index} />;
        case "pricing": return <Pricing block={block} index={index} key={index} />;
        case "cta": return <Cta block={block} key={index} />;
        case "people": return <People block={block} key={index} />;
        case "listings": return <Listings block={block} listings={listings} key={index} />;
        case "handled": return <Handled block={block} key={index} />;
        case "statTiles": return <StatTiles block={block} key={index} />;
        case "inlineCta": return <InlineCta block={block} key={index} />;
        case "partnerDoors": return <PartnerDoors block={block} introducerId={view.partnerIntroducerId} hosts={view.partnerLinkHosts} key={index} />;
    }
};

const BUYER_PATHS = ["/buying", "/listings"];

const stickyAction = (path: string, view: MarketingView.T): JSX.Element => {
    const primary = view.navLinks.filter((link) => link.button)[0];

    if (view.brand === "sail_legal") {
        return <a className="marketingSticky__cta" href={primary.url}>{primary.text}</a>;
    }
    return BUYER_PATHS.includes(path)
        ? <a className="marketingSticky__cta" href="/listings">See properties for sale</a>
        : <a className="marketingSticky__cta" href="/valuation/your-details">Get my free valuation</a>;
};

const STICKY_SCRIPT = `(function(){
  var bar=document.getElementById('marketing-sticky');
  if(!bar){return;}
  var hero=document.querySelector('.marketing__hero-action');
  var close=document.querySelector('.marketing__section--cta');
  function tick(){
    var past=hero?hero.getBoundingClientRect().bottom<0:window.pageYOffset>600;
    var at=close?close.getBoundingClientRect().top<window.innerHeight:false;
    bar.className='marketingSticky'+((past&&!at)?' marketingSticky--on':'');
  }
  window.addEventListener('scroll',tick,{passive:true});
  window.addEventListener('resize',tick);
  tick();
})();`;

export const MarketingAppContainer = (props: { view: MarketingView.T }): JSX.Element => {
    const isBare = props.view.page.chrome === "bare" && props.view.partner !== null;

    return <div className={`marketing${props.view.brand === "sail_legal" ? " marketing--sailLegal" : ""}`}>
        {isBare && props.view.partner !== null
            ? <MarketingPartnerNav view={props.view} partner={props.view.partner} />
            : <MarketingNav view={props.view} />}
        <main className="marketing__main">
            {props.view.page.blocks.map((block, i) => renderBlock(block, i, props.view.statistic, props.view.listings, props.view))}
        </main>
        <MarketingFooter view={props.view} />

        {/* Appears once the hero capture has scrolled away and hides again at the closing
            CTA, so it never covers the button it duplicates. Progressive enhancement:
            without JS it simply never shows.

            The ask follows the page's audience. A buyer testing the site put it plainly:
            being shown "Get my free valuation" on a page about buying is "like walking into
            a shop where every till only takes the other currency". */}
        {isBare
            ? null
            : <>
                <div className="marketingSticky" id="marketing-sticky">
                    <a className="marketingSticky__call" href={props.view.phoneUrl}>Call</a>
                    {stickyAction(props.view.page.path, props.view)}
                </div>
                <script dangerouslySetInnerHTML={{ __html: STICKY_SCRIPT }} />
            </>}
    </div>;
};
